'use client';

import { c as _c } from "react/compiler-runtime";
import React, { useContext } from 'react';
import ReactGA from 'react-ga4';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useTranslations } from 'next-intl';
import { usePathname } from 'next/navigation';
import { Newspaper } from 'lucide-react';
import { amplitudeInstance } from '../../config/amplitude';
import AuthContext from '../../context/AuthContext';
import buildRouteUrl from '../../helpers/buildRouteUrl';
import useIntl from '../../hooks/useIntl';
import useModal from '../../hooks/useModal';
import { Link } from '../../i18n/navigation';
import * as fbq from '../../lib/fpixel';
import BrandLogo from '../BrandLogo';
import Search from '../Search';
// import SubscriptionsPlansModal from '../SubscriptionPlansModal';

function Header() {
  const $ = _c(69);
  const pathname = usePathname();
  const isNewsPage = /\/(?:news|noticias)(?:\/|$)/.test(pathname);
  const intl = useIntl();
  const t = useTranslations();
  const auth = useContext(AuthContext);
  let pathnameWithoutLocale;
  if ($[0] !== intl.locale || $[1] !== pathname) {
    pathnameWithoutLocale = pathname.replace(`${intl.locale}`, "");
    if (pathnameWithoutLocale.includes("//")) {
      pathnameWithoutLocale = pathnameWithoutLocale.slice(1);
    }
    $[0] = intl.locale;
    $[1] = pathname;
    $[2] = pathnameWithoutLocale;
  } else {
    pathnameWithoutLocale = $[2];
  }
  let t0;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = buildRouteUrl("/");
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  let t1;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Link href={t0} className="mr-2 md:mr-8" prefetch={true}><BrandLogo className="h-[22px] w-[144px]" priority={true} /></Link>;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = buildRouteUrl("/");
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const t3 = `no-underline ${pathnameWithoutLocale === "/" ? "text-white font-bold" : "text-[#666] hover:opacity-[0.5] regular"}`;
  let t4;
  if ($[6] !== t) {
    t4 = t("header.links.home");
    $[6] = t;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t3 || $[9] !== t4) {
    t5 = <Link href={t2} className={t3} prefetch={false}>{t4}</Link>;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== auth.isLoading || $[12] !== auth.isLoggedIn || $[13] !== pathnameWithoutLocale || $[14] !== t) {
    t6 = !auth.isLoading && auth.isLoggedIn && <Link href={buildRouteUrl("/my-progress")} className={`no-underline ${pathnameWithoutLocale === "/my-progress" ? "text-white font-bold" : "text-[#666] hover:opacity-[0.5] regular"}`} prefetch={true}>{t("header.links.my_progress")}</Link>;
    $[11] = auth.isLoading;
    $[12] = auth.isLoggedIn;
    $[13] = pathnameWithoutLocale;
    $[14] = t;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== auth.isLoading || $[17] !== auth.isLoggedIn || $[18] !== pathnameWithoutLocale || $[19] !== t) {
    t7 = !auth.isLoading && auth.isLoggedIn && <Link href={buildRouteUrl("/my-list")} className={`no-underline ${pathnameWithoutLocale === "/my-list" ? "text-white font-bold" : "text-[#666] hover:opacity-[0.5] regular"}`} prefetch={true}>{t("header.links.my_list")}</Link>;
    $[16] = auth.isLoading;
    $[17] = auth.isLoggedIn;
    $[18] = pathnameWithoutLocale;
    $[19] = t;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  const t8 = isNewsPage ? "page" : undefined;
  const t9 = `inline-flex items-center gap-1.5 whitespace-nowrap no-underline transition-colors hover:text-white ${isNewsPage ? "font-bold text-white" : "text-[#999]"}`;
  let t10;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <Newspaper size={16} aria-hidden="true" />;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== t8 || $[23] !== t9) {
    t11 = <a href="/br/noticias" aria-current={t8} className={t9}>{t10}Notícias</a>;
    $[22] = t8;
    $[23] = t9;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== t11 || $[26] !== t5 || $[27] !== t6 || $[28] !== t7) {
    t12 = <div className="flex flex-row items-center mr-8">{t1}<div className="hidden md:flex flex-row gap-x-3">{t5}{t6}{t7}{t11}</div></div>;
    $[25] = t11;
    $[26] = t5;
    $[27] = t6;
    $[28] = t7;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <div className="hidden md:flex flex-row flex-1 justify-end"><div className="md:w-[80%]"><Search /></div></div>;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== auth || $[32] !== pathname || $[33] !== t) {
    t14 = <div className="flex flex-row items-center ml-0 md:ml-6">{auth.isLoggedIn ? <DropdownMenu.Root><DropdownMenu.Trigger><span className="text-white hover:text-[#666] font-bold">{auth.userData?.name}</span></DropdownMenu.Trigger><DropdownMenu.Portal style={{
          zIndex: 100
        }} className="z-10"><DropdownMenu.Content align="end" sideOffset={5} className="w-48 rounded-lg p-2 shadow-md md:w-56 bg-black z-10"><DropdownMenu.Item className="flex items-center rounded-md px-2 h-10 text-sm outline-none focus:bg-gray-200 cursor-pointer"><Link href={buildRouteUrl("/users/me")} className="flex-grow text-white" prefetch={true}>{t("profile.title")}</Link></DropdownMenu.Item><DropdownMenu.Item className="flex items-center rounded-md px-2 h-10 text-sm outline-none focus:bg-gray-200 cursor-pointer"><Link href={buildRouteUrl("/settings")} className="flex-grow text-white" prefetch={true}>{t("profile.settings")}</Link></DropdownMenu.Item><DropdownMenu.Item className="flex items-center rounded-md px-2 h-10 text-sm outline-none focus:bg-gray-200 cursor-pointer"><span onClick={() => auth.destroySession()} className="flex-grow text-white">{t("profile.logout")}</span></DropdownMenu.Item></DropdownMenu.Content></DropdownMenu.Portal></DropdownMenu.Root> : <Link onClick={() => amplitudeInstance.logEvent("Header Go To Login Button", {
        page_path: pathname
      })} href={buildRouteUrl("/login")} className="md:ml-4 text-white hover:text-gray-200 font-semibold uppercase">Login</Link>}</div>;
    $[31] = auth;
    $[32] = pathname;
    $[33] = t;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  let t15;
  if ($[35] !== t12 || $[36] !== t14) {
    t15 = <div className="flex flex-row justify-between items-center px-4 md:px-10 py-4 w-full h-[64px] min-h-[64px]">{t12}{t13}{t14}</div>;
    $[35] = t12;
    $[36] = t14;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  let t16;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = buildRouteUrl("/");
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  const t17 = `no-underline ${pathname === "/" ? "text-white font-bold" : "text-[#666] regular"}`;
  let t18;
  if ($[39] !== t) {
    t18 = t("header.links.home");
    $[39] = t;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  let t19;
  if ($[41] !== t17 || $[42] !== t18) {
    t19 = <Link href={t16} className={t17} prefetch={false}>{t18}</Link>;
    $[41] = t17;
    $[42] = t18;
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  let t20;
  if ($[44] !== auth.isLoading || $[45] !== auth.isLoggedIn || $[46] !== pathname || $[47] !== t) {
    t20 = !auth.isLoading && auth.isLoggedIn && <Link href={buildRouteUrl("/my-progress")} className={`no-underline ${pathname === "/my-progress" ? "text-white font-bold" : "text-[#666] regular"}`} prefetch={true}>{t("header.links.my_progress")}</Link>;
    $[44] = auth.isLoading;
    $[45] = auth.isLoggedIn;
    $[46] = pathname;
    $[47] = t;
    $[48] = t20;
  } else {
    t20 = $[48];
  }
  let t21;
  if ($[49] !== auth.isLoading || $[50] !== auth.isLoggedIn || $[51] !== pathname || $[52] !== t) {
    t21 = !auth.isLoading && auth.isLoggedIn && <Link href={buildRouteUrl("/my-list")} className={`no-underline ${pathname === "/my-list" ? "text-white font-bold" : "text-[#666] regular"}`} prefetch={true}>{t("header.links.my_list")}</Link>;
    $[49] = auth.isLoading;
    $[50] = auth.isLoggedIn;
    $[51] = pathname;
    $[52] = t;
    $[53] = t21;
  } else {
    t21 = $[53];
  }
  const t22 = isNewsPage ? "page" : undefined;
  const t23 = `inline-flex items-center gap-1.5 whitespace-nowrap no-underline transition-colors hover:text-white ${isNewsPage ? "font-bold text-white" : "text-[#999]"}`;
  let t24;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <Newspaper size={16} aria-hidden="true" />;
    $[54] = t24;
  } else {
    t24 = $[54];
  }
  let t25;
  if ($[55] !== t22 || $[56] !== t23) {
    t25 = <a href="/br/noticias" aria-current={t22} className={t23}>{t24}Notícias</a>;
    $[55] = t22;
    $[56] = t23;
    $[57] = t25;
  } else {
    t25 = $[57];
  }
  let t26;
  if ($[58] !== t19 || $[59] !== t20 || $[60] !== t21 || $[61] !== t25) {
    t26 = <div className="flex min-w-0 flex-1 mr-3"><div className="flex min-w-0 flex-row items-center gap-x-3 overflow-x-auto whitespace-nowrap text-sm">{t19}{t20}{t21}{t25}</div></div>;
    $[58] = t19;
    $[59] = t20;
    $[60] = t21;
    $[61] = t25;
    $[62] = t26;
  } else {
    t26 = $[62];
  }
  let t27;
  if ($[63] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <div className="md:ml-4"><Search /></div>;
    $[63] = t27;
  } else {
    t27 = $[63];
  }
  let t28;
  if ($[64] !== t26) {
    t28 = <div className="flex md:hidden flex-row justify-between items-center px-4 md:px-10 py-4 bg-black w-full h-[56px] min-h-[56px]">{t26}{t27}</div>;
    $[64] = t26;
    $[65] = t28;
  } else {
    t28 = $[65];
  }
  let t29;
  if ($[66] !== t15 || $[67] !== t28) {
    t29 = <><div className="flex flex-col w-screen min-w-full justify-between items-center z-10 fixed bg-black">{t15}{t28}</div></>;
    $[66] = t15;
    $[67] = t28;
    $[68] = t29;
  } else {
    t29 = $[68];
  }
  return t29;
}
export default Header;